import { signupConsentSchema, type SignupConsent } from "@shared/signupConsent";
import { effectivePlanTier } from "@shared/planAccess";
import { testerTrialForSignup, withTesterTier } from "@shared/testerCampaign";
import { setOfflineUser } from "../lib/offlineStorage";
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "../lib/supabase";
import { clearAllUserData, authStorage } from "../lib/localStorage";
import { rememberSignedOutAppearance } from "@/themes/appearance";
import type { User, Session } from "@supabase/supabase-js";
import type { AuthUser } from "../../../shared/api";
import {
  normalizeEmailClient,
  sanitizePasswordForWire,
  sanitizePlainText,
} from "@/utils/sanitizeInput";
import { posthog } from "../lib/posthog";
import { track } from "../lib/analytics/analytics";
import { ANALYTICS_EVENTS } from "../lib/analytics/events";
import { hashString } from "../lib/hash";
import { authFetch } from "../lib/auth";
import { queryClient } from "../lib/queryClient";
import { toast } from "sonner";

interface AuthContextType {
  // State
  user: AuthUser | null;
  session: Session | null;
  isLoading: boolean;
  authReady: boolean;
  isAuthenticated: boolean;
  isOfflineMode: boolean;

  // Operation-specific loading states
  isLoggingIn: boolean;
  isSigningUp: boolean;
  isLoggingOut: boolean;
  isLoggingInWithGoogle: boolean;
  initStatus: string;

  // Error states
  loginError: string | null;
  signupError: string | null;
  logoutError: string | null;
  googleLoginError: string | null;
  needsAvatarMigration: boolean;
  dismissAvatarMigration: () => void;

  // Actions
  login: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signup: (email: string, password: string, fullName?: string, username?: string, captchaToken?: string, consent?: SignupConsent) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  deleteAccount: () => Promise<void>;

  // Utilities
  refreshSession: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshUser: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: { 
    full_name?: string; 
    avatar_url?: string; 
    username?: string; 
    avatar_seed?: string;
    avatar_provider?: string;
    avatar_version?: string;
    avatar_style?: string;
    avatar_options?: Record<string, unknown>;
    target_exam?: string;
    personalized_learning_summary?: any;
  }) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<any>;

  // Error clearing
  clearErrors: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

let hasIdentifiedPostHog = false;
// Distinguishes a deliberate sign-out from a session that expired or was
// revoked, so we can tell the user why they suddenly landed on /login.
let isExplicitSignOut = false;
let wasAuthenticated = false;

const AVATAR_FIELDS = ["avatar_seed", "avatar_provider", "avatar_version", "avatar_style", "avatar_options", "avatar_url"] as const;
const avatarFields = (source: Partial<AuthUser>) => Object.fromEntries(AVATAR_FIELDS.map(key => [key, source[key]])) as Partial<AuthUser>;

// Friendly error messages for common auth errors
function getFriendlyErrorMessage(error: any): string {
  if (!error) return "That didn't work. Try again.";

  const message = error.message || String(error);

  // Supabase auth errors are already user-friendly
  if (message.includes("Invalid login credentials")) return "Invalid email or password";
  // Deliberately generic: confirming an email is already registered lets an
  // attacker enumerate accounts by trying addresses one at a time. Signup.tsx
  // already masked this at its own call site, but that only holds as long as
  // every future caller remembers to re-mask it too — collapsing it here, at
  // the source, means signupError can never carry the revealing string in
  // the first place. Found 2026-09-08 during an account-enumeration review.
  if (message.includes("Email already registered") || message.includes("User already exists")) {
    return "That didn't go through — try signing in instead.";
  }
  if (message.includes("Network error")) return "Connection error. Please check your internet and try again.";
  if (message.includes("Timeout")) return "Request timed out. Please try again.";
  if (message.includes("Too many requests")) return "Too many attempts. Please wait a moment and try again.";
  if (message.includes("Email not confirmed")) return "Please confirm your email before signing in";

  // For technical errors, show a generic message instead of raw error
  if (message.includes("PGRST") || message.includes("fetch") || message.includes("ERR")) {
    return "We're having trouble reaching Novari right now. Try again in a moment.";
  }

  // Pass through other messages (usually already friendly from Supabase)
  return message;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const userRef = React.useRef(user);
  userRef.current = user;
  const profileQueue = React.useRef<Promise<void>>(Promise.resolve());
  const profileRevision = React.useRef(0);
  const avatarSequence = React.useRef(0);
  const pendingAvatar = React.useRef<Partial<AuthUser> | null>(null);
  const confirmedAvatar = React.useRef<Partial<AuthUser>>({});

  const saveUser = (next: AuthUser) => authStorage.saveUser(pendingAvatar.current ? { ...next, ...confirmedAvatar.current } : next);
  const [isLoading, setIsLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isLoggingInWithGoogle, setIsLoggingInWithGoogle] = useState(false);
  const [initStatus, setInitStatus] = useState("Initializing...");

  const [loginError, setLoginError] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [googleLoginError, setGoogleLoginError] = useState<string | null>(null);
  const [needsAvatarMigration, setNeedsAvatarMigration] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  const getProfileByAuthUserId = React.useCallback(async (authUserId: string) => {
    const revision = profileRevision.current;
    // Sequential query pattern: try auth_user_id first, then fallback to id
    // Avoids PostgREST relationship discovery errors from complex OR clauses
    let { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("auth_user_id", authUserId)
      .maybeSingle();

    if (error && error.code !== "PGRST116") throw error; // PGRST116 = no rows, which is fine
    if (data) return revision === profileRevision.current ? data : null;

    // Fallback: try matching by id field if auth_user_id didn't work
    ({ data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", authUserId)
      .maybeSingle());

    if (error) throw error;
    return revision === profileRevision.current ? data : null;
  }, []);

  const mapSupabaseUser = (supabaseUser: any, profile?: any): AuthUser => {
    const metadata = supabaseUser.user_metadata || {};
    const googlePicture = metadata.picture || metadata.avatar_url;
    const currentAvatar = profile?.avatar_url || metadata.avatar_url || "";
    const cached = userRef.current?.id === supabaseUser.id ? userRef.current : authStorage.getUser();
    const avatar = { ...(profile || (cached?.id === supabaseUser.id ? cached : {})), ...pendingAvatar.current };
    const testerTrial = testerTrialForSignup(supabaseUser.id, supabaseUser.created_at);
    const billingTier = effectivePlanTier({ plan_tier: profile?.plan_tier || metadata.plan_tier, subscription_expiry: profile?.subscription_expiry });

    return {
      id: supabaseUser.id,
      email: supabaseUser.email || "",
      full_name: profile?.full_name || metadata.full_name || metadata.name || "",
      username: profile?.username || metadata.username || supabaseUser.email?.split('@')[0] || "",
      avatar_url: googlePicture || currentAvatar,
      phone: supabaseUser.phone,
      email_confirmed_at: supabaseUser.email_confirmed_at,
      last_sign_in_at: supabaseUser.last_sign_in_at,
      created_at: supabaseUser.created_at,
      updated_at: supabaseUser.updated_at,
      onboarding_completed: metadata.onboarding_completed || false,
      // The tier in force: a lapsed paid plan is Starter immediately, not after the
      // expiry job tidies the row, so every screen agrees with the server.
      plan_tier: withTesterTier(billingTier, testerTrial),
      billing_plan_tier: billingTier,
      tester_trial: testerTrial,
      profile_loaded: !!profile,
      country_code: profile?.country_code || undefined,
      country: profile?.country || metadata.country || "Global",
      target_exam: profile?.target_exam || metadata.target_exam || "Other",
      currency: profile?.currency || metadata.currency || "USD",
      daily_usage: profile?.daily_usage || metadata.daily_usage || { chat_count: 0, quiz_count: 0, deck_count: 0, last_reset: new Date().toISOString().split('T')[0] },
      avatar_seed: avatar.avatar_seed || metadata.avatar_seed || "",
      avatar_provider: avatar.avatar_provider || metadata.avatar_provider || null,
      avatar_version: avatar.avatar_version || metadata.avatar_version || null,
      avatar_style: avatar.avatar_style || metadata.avatar_style || null,
      avatar_options: avatar.avatar_options || metadata.avatar_options || null,
      avatar_migrated_at: profile?.avatar_migrated_at || metadata.avatar_migrated_at || null,
      trial_ends_at: testerTrial?.endsAt || profile?.trial_ends_at || metadata.trial_ends_at || null,
      subscription_expiry: profile?.subscription_expiry || null,
      personalized_learning_summary: profile?.personalized_learning_summary || null,
      last_paid_tier: profile?.last_paid_tier || null,
      last_paid_tier_lost_at: profile?.last_paid_tier_lost_at || null,
    };
  };

  // Expire the visible allocation even when a tester leaves the app open.
  useEffect(() => {
    const ownerId = user?.id;
    const endsAt = user?.tester_trial?.endsAt;
    if (!ownerId || !endsAt || !user?.tester_trial?.active) return;
    const timeout = setTimeout(() => {
      const previous = userRef.current;
      if (!previous || previous.id !== ownerId) return;
      const trial = testerTrialForSignup(previous.id, previous.created_at);
      const updated = { ...previous, plan_tier: effectivePlanTier(previous), tester_trial: trial };
      userRef.current = updated;
      setUser(updated);
      authStorage.saveUser(pendingAvatar.current ? { ...updated, ...confirmedAvatar.current } : updated);
    }, Math.max(0, Math.min(2_147_483_647, Date.parse(endsAt) - Date.now())));
    return () => clearTimeout(timeout);
  }, [user?.id, user?.tester_trial?.endsAt, user?.tester_trial?.active]);

  const _log = (code: string, data?: any) => {
    if (import.meta.env.DEV) console.log(`[AC:${code}]`, data !== undefined ? data : '');
  };
  const _warn = (code: string, data?: any) => {
    if (import.meta.env.DEV) console.warn(`[AC:${code}]`, data !== undefined ? data : '');
  };
  const _err = (code: string, data?: any) => {
    if (import.meta.env.DEV) console.error(`[AC:${code}]`, data !== undefined ? data : '');
  };

  useEffect(() => {
    let authRevision = 0;
    let disposed = false;
    let activeAccountId: string | null | undefined;
    const withRetry = async <T,>(fn: () => Promise<T>, opCode: string, maxRetries = 2, baseDelayMs = 1000): Promise<T> => {
      let lastError: any;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const start = Date.now();
          const result = await Promise.race([
            fn(),
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Auth Timeout')), 5000))
          ]);
          _log(`${opCode}:OK`, Date.now() - start);
          return result;
        } catch (error: any) {
          lastError = error;
          if (attempt < maxRetries) {
            const delay = baseDelayMs * Math.pow(2, attempt - 1);
            _log('07', { op: opCode, a: attempt, d: delay });
            await new Promise(r => setTimeout(r, delay));
          }
        }
      }
      throw lastError;
    };

    const initAuth = async () => {
      const revision = authRevision;
      const t0 = Date.now();
      try {
        setInitStatus("Connecting...");
        // getSession reads the locally persisted, refreshable session. getUser
        // requires a network round-trip and can be slow during a cold Supabase
        // connection. A timeout there must not turn a healthy local session into
        // a false sign-out (which previously produced a blank protected route
        // before the user was sent back to Login).
        const sessionResult = await withRetry(() => supabase.auth.getSession(), '02');
        const currentSession = sessionResult.data.session;
        let verifiedUser = currentSession?.user ?? null;

        try {
          const userResult = await withRetry(() => supabase.auth.getUser(), '03', 2, 4000);
          verifiedUser = userResult.data.user ?? verifiedUser;
        } catch (userError: any) {
          if (!verifiedUser) throw userError;
          _warn('03:SESSION_FALLBACK', userError?.message);
        }
        if (disposed || revision !== authRevision) return;
        activeAccountId = currentSession?.user.id ?? null;
        await setOfflineUser(activeAccountId);
        if (disposed || revision !== authRevision) return;
        setSession(currentSession);

        if (currentSession && verifiedUser) {
          const profilePromise = withRetry(async () => getProfileByAuthUserId(verifiedUser.id), '04');

          // Use optimistic profile if available or wait briefly
          const profileResult = await Promise.race([
            profilePromise,
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000))
          ]);
          if (disposed || revision !== authRevision) return;

          // If the 2s ceiling won the race, the real profile is still in
          // flight — NEVER discard it. Falling back to mapSupabaseUser(user,
          // null) here means plan_tier/full_name/avatar/etc. all collapse to
          // defaults ("starter", "", ...) for the WHOLE app until something
          // else happens to re-fetch. That false "starter" reading is what
          // was tripping the post-purchase upgrade celebration for users who
          // had been on Plus/Max for weeks: the moment the real profile
          // eventually arrived (via the realtime profile sync below, or a
          // later refreshProfile()), plan_tier appeared to "increase" from
          // the stale starter fallback. Once the real fetch resolves, correct
          // the user object in place instead of leaving it wrong all session.
          const usedFallback = profileResult === null;
          if (usedFallback) {
            profilePromise
              .then((realProfile) => {
                if (!realProfile || disposed || revision !== authRevision) return;
                const correctedUser = mapSupabaseUser(verifiedUser, realProfile);
                setUser(correctedUser);
                saveUser(correctedUser);
              })
              .catch((err) => _warn('04:LATE', err?.message));
          }

          const mappedUser = mapSupabaseUser(verifiedUser, profileResult);
          setUser(mappedUser);
          saveUser(mappedUser);

          // Identify user in PostHog (Hashed PII)
          if (!hasIdentifiedPostHog) {
            hasIdentifiedPostHog = true;
            Promise.all([
              hashString(mappedUser.id),
              hashString(mappedUser.email)
            ]).then(([hashedId, hashedEmail]) => {
                  if (disposed || revision !== authRevision) return;
              try {
                posthog.identify(hashedId, {
                  hashed_email: hashedEmail,
                  plan_tier: mappedUser.plan_tier,
                });
              } catch (err) {
                _warn('PH:ID:FAIL', err);
              }
            }).catch(err => {
              hasIdentifiedPostHog = false;
              _warn('PH:HASH:FAIL', err);
            });
          }

          // Only accounts with persisted legacy avatar data qualify. New users
          // have no old image/seed and are assigned DiceBear during onboarding.
          const hasPersistedLegacyAvatar = Boolean(profileResult?.avatar_url) ||
            (Boolean(profileResult?.avatar_seed) && profileResult?.avatar_seed !== "default");
          const isLegacyAvatarUser = hasPersistedLegacyAvatar &&
            profileResult?.avatar_provider !== "dicebear" && !profileResult?.avatar_migrated_at;
          if (!authStorage.hasAvatarMigrated() && isLegacyAvatarUser) {
            setNeedsAvatarMigration(true);
          }
        } else {
          setUser(null);
          authStorage.clearUser();
        }
      } catch (error: any) {
        if (disposed || revision !== authRevision) return;
        _err('0A', error?.message);
        const savedUser = authStorage.getUser();
        if (savedUser && !navigator.onLine) {
          await setOfflineUser(savedUser.id);
          if (disposed || revision !== authRevision) return;
          setUser(savedUser);
          setIsOfflineMode(true);
        } else {
          setUser(null);
          setSession(null);
          authStorage.clearUser();
        }
      } finally {
        if (!disposed) {
          setIsLoading(false);
          setAuthReady(true);
        }
      }
    };

    initAuth();

    const safetyTimeout = setTimeout(() => {
      setIsLoading(loading => {
        if (loading) {
          _warn('08');
          setAuthReady(true);
          return false;
        }
        return loading;
      });
    }, 5000);

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const revision = ++authRevision;
      const nextAccountId = session?.user.id ?? null;
      if (activeAccountId && activeAccountId !== nextAccountId) {
        pendingAvatar.current = null;
        confirmedAvatar.current = {};
        clearAllUserData();
        queryClient.clear();
        posthog.reset();
        hasIdentifiedPostHog = false;
      }
      activeAccountId = nextAccountId;
      void setOfflineUser(session?.user.id ?? null);
      setSession(session);
      if (session) {
        wasAuthenticated = true;
        // Set user immediately with Supabase user data to avoid logout during profile fetch
        // This ensures the user stays logged in even if profile fetch is slow or fails
        let newUser = mapSupabaseUser(session.user, null);
        setUser(previous => previous?.id === session.user.id ? { ...newUser, ...previous, ...pendingAvatar.current } : newUser);
        saveUser(newUser);

        // Fetch profile in parallel without blocking UI loading state
        Promise.resolve(getProfileByAuthUserId(session.user.id))
          .then((profile) => {
            if (disposed || revision !== authRevision) return;
            if (profile) {
              // Update user with full profile data
              newUser = mapSupabaseUser(session.user, profile);
              setUser(newUser);
              saveUser(newUser);
            }

            // Side effects in a safe try-catch block
            try {
              // Identify in PostHog (Hashed PII)
              if (!hasIdentifiedPostHog) {
                hasIdentifiedPostHog = true;
                Promise.all([
                  hashString(newUser.id),
                  hashString(newUser.email)
                ]).then(([hashedId, hashedEmail]) => {
                  if (disposed || revision !== authRevision) return;
                  try {
                    posthog.identify(hashedId, {
                      hashed_email: hashedEmail,
                      plan_tier: newUser.plan_tier,
                    });
                  } catch (err) {
                    _warn('PH:ID:FAIL', err);
                  }
                }).catch((err: any) => {
                  hasIdentifiedPostHog = false;
                  _warn('PH:HASH:FAIL', err);
                });
              }
            } catch (err) {
              _warn('AUTH:SIDE_EFFECTS:FAIL', err);
            }
          })
          .catch((err: any) => {
            _err('PROFILE:FETCH:FAIL', err);
            // Even if profile fetch fails, user is already set from Supabase data
            // so they won't be logged out
          });
      } else {
        // Tell the user why they were signed out when it wasn't their doing
        if (event === "SIGNED_OUT" && wasAuthenticated && !isExplicitSignOut) {
          toast.error("Your session expired. Please sign in again.");
        }
        wasAuthenticated = false;
        isExplicitSignOut = false;
        setUser(null);
        authStorage.clearUser();
        clearAllUserData();
        queryClient.clear();
        try {
          posthog.reset();
          hasIdentifiedPostHog = false;
        } catch (err) {
          _warn('PH:RESET:FAIL', err);
        }
      }
      setIsOfflineMode(false);
      setIsLoading(false);
      setAuthReady(true);
    });

    let profileSubscription: any = null;
    const setupRealtimeProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      // StrictMode double-mounts effects in dev: each mount's setupRealtimeProfile
      // call has its own `profileSubscription` closure variable, so it can't see
      // a channel created by a *previous* mount, and supabase-js reuses a channel
      // object by topic name — the second mount's `.on()` would hit an
      // already-subscribed channel and throw "cannot add postgres_changes
      // callbacks after subscribe()". Look up any existing channel for this user
      // on the client-wide registry (which persists across mounts) and tear it
      // down before creating a fresh one.
      const topic = `profile_sync_${session.user.id}`;
      const existing = supabase.getChannels().find((c) => c.topic === `realtime:${topic}`);
      if (existing) {
        await supabase.removeChannel(existing);
      }

      profileSubscription = supabase
        .channel(topic)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `user_id=eq.${session.user.id}` }, (payload) => {
          if (
            payload.new?.id !== session.user.id &&
            payload.new?.auth_user_id !== session.user.id
          ) {
            return;
          }
          setUser(prev => {
            if (!prev || prev.id !== session.user.id) return prev;
            const updated = mapSupabaseUser(session.user, { ...prev, ...payload.new });
            saveUser(updated);
            return updated;
          });
        })
        .subscribe();
    };
    setupRealtimeProfile();

    const handleOnline = async () => {
      setIsOfflineMode(false);
      try { await supabase.auth.refreshSession(); } catch (e) { setIsOfflineMode(true); }
    };

    // supabase-js autoRefreshToken already renews the session before expiry.
    window.addEventListener('online', handleOnline);

    return () => {
      disposed = true;
      authRevision++;
      clearTimeout(safetyTimeout);
      authSubscription.unsubscribe();
      if (profileSubscription) profileSubscription.unsubscribe();
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const dismissAvatarMigration = React.useCallback(() => {
    setNeedsAvatarMigration(false);
    authStorage.setAvatarMigrated();
  }, []);

  const login = React.useCallback(async (email: string, password: string, captchaToken?: string) => {
    try {
      setIsLoggingIn(true);
      setLoginError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email: normalizeEmailClient(email),
        password: sanitizePasswordForWire(password),
        options: {
          captchaToken:
            sanitizePlainText(captchaToken || "", 4096, { trim: false }) || "",
        },
      });
      if (error) throw error;
    } catch (error: any) {
      const friendlyMessage = getFriendlyErrorMessage(error);
      setLoginError(friendlyMessage);
      throw error;
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  const signup = React.useCallback(async (email: string, password: string, fullName?: string, username?: string, captchaToken?: string, consent?: SignupConsent) => {
    try {
      setIsSigningUp(true);
      setSignupError(null);
      const registration = signupConsentSchema.parse(consent);
      const { error } = await supabase.auth.signUp({
        email: normalizeEmailClient(email),
        password: sanitizePasswordForWire(password),
        options: {
          data: {
            ...registration,
            full_name: fullName ? sanitizePlainText(fullName, 200) : "",
            username: username
              ? sanitizePlainText(username.toLowerCase(), 64)
              : "",
          },
          captchaToken:
            sanitizePlainText(captchaToken || "", 4096, { trim: false }) || "",
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error: any) {
      const friendlyMessage = getFriendlyErrorMessage(error);
      setSignupError(friendlyMessage);
      throw error;
    } finally {
      setIsSigningUp(false);
    }
  }, []);

  const loginWithGoogle = React.useCallback(async () => {
    try {
      setIsLoggingInWithGoogle(true);
      setGoogleLoginError(null);
      const redirectUrl = `${window.location.origin}/auth/callback`;
      // Use 'select_account' instead of 'consent' to allow account selection without forcing consent every time
      // This improves UX when cookies are cleared - user can still select the same account
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: { access_type: 'offline', prompt: 'select_account' },
          skipBrowserRedirect: false
        }
      });
      if (error) throw error;
    } catch (error: any) {
      const friendlyMessage = getFriendlyErrorMessage(error);
      setGoogleLoginError(friendlyMessage);
      track(ANALYTICS_EVENTS.AUTH_ERROR, { flow: "google_oauth", message: error.message });
      throw error;
    } finally {
      setIsLoggingInWithGoogle(false);
    }
  }, []);

  const logout = React.useCallback(async () => {
    try {
      rememberSignedOutAppearance();
      setIsLoggingOut(true);
      isExplicitSignOut = true;
      await setOfflineUser(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setSession(null);
      authStorage.clearUser();
      clearAllUserData();
      queryClient.clear();
      hasIdentifiedPostHog = false;
      posthog.reset();
    } catch (error: any) {
      isExplicitSignOut = false;
      setLogoutError(error.message);
      throw error;
    } finally {
      setIsLoggingOut(false);
    }
  }, []);

  const deleteAccount = React.useCallback(async () => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) throw new Error("Not authenticated");

      const response = await authFetch('/api/auth/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || "Failed to delete account");
      }

      await logout();
    } catch (error: any) {
      console.error("Delete account error:", error);
      track(ANALYTICS_EVENTS.AUTH_ERROR, { flow: "delete_account", message: error.message });
      throw error;
    }
  }, []);

  const clearErrors = React.useCallback(() => {
    setLoginError(null);
    setSignupError(null);
    setLogoutError(null);
    setGoogleLoginError(null);
  }, []);

  const refreshSession = React.useCallback(async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    if (data.session) setSession(data.session);
  }, []);

  const refreshProfile = React.useCallback(async () => {
    if (!user?.id) return;
    const profile = await getProfileByAuthUserId(user.id);
    if (profile && session?.user) {
      const updatedUser = mapSupabaseUser(session.user, profile);
      setUser(updatedUser);
      saveUser(updatedUser);
    }
  }, [user?.id, session?.user, getProfileByAuthUserId]);

  const refreshUser = React.useCallback(async () => {
    const { data: { user: supabaseUser }, error } = await supabase.auth.getUser();
    if (error) throw error;
    if (supabaseUser) {
      const profile = await getProfileByAuthUserId(supabaseUser.id);
      const mappedUser = mapSupabaseUser(supabaseUser, profile);
      setUser(mappedUser);
      saveUser(mappedUser);
    }
  }, [getProfileByAuthUserId]);

  const resetPassword = React.useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/update-password` });
    if (error) throw error;
  }, []);

  const updateProfile = React.useCallback(async (updates: { 
    full_name?: string; 
    avatar_url?: string; 
    username?: string; 
    avatar_seed?: string;
    avatar_provider?: string;
    avatar_version?: string;
    avatar_style?: string;
    avatar_options?: Record<string, unknown>;
    country?: string;
    target_exam?: string;
    personalized_learning_summary?: any;
  }) => {
    const owner = userRef.current;
    if (!owner?.id) throw new Error('No authenticated user');
    const changesAvatar = AVATAR_FIELDS.some(key => key in updates);
    const sequence = changesAvatar ? ++avatarSequence.current : avatarSequence.current;
    if (changesAvatar) {
      if (!pendingAvatar.current) confirmedAvatar.current = avatarFields(owner);
      pendingAvatar.current = { ...pendingAvatar.current, ...avatarFields({ ...owner, ...updates }) };
      profileRevision.current++;
      const optimistic = { ...owner, ...pendingAvatar.current };
      userRef.current = optimistic;
      setUser(optimistic);
    }

    const write = async () => {
      if (userRef.current?.id !== owner.id) throw new Error('Your account changed before the profile could save.');
      try {
        const res = await authFetch("/api/profile", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || "Failed to update profile data");
        const profile = body.context?.profile;
        if (changesAvatar && (!profile || (updates.avatar_seed !== undefined && profile.avatar_seed !== updates.avatar_seed) || (updates.avatar_style !== undefined && profile.avatar_style !== updates.avatar_style))) {
          throw new Error("Your avatar was not confirmed. Please try again.");
        }
        if (userRef.current?.id !== owner.id) return;
        profileRevision.current++;
        const canonical = { ...userRef.current, ...profile, id: owner.id, email: owner.email, profile_loaded: !!profile };
        if (changesAvatar) {
          confirmedAvatar.current = avatarFields(canonical);
          if (sequence === avatarSequence.current) pendingAvatar.current = null;
        }
        authStorage.saveUser(canonical);
        const visible = { ...canonical, ...pendingAvatar.current };
        userRef.current = visible;
        setUser(visible);
        void queryClient.invalidateQueries({ queryKey: ['user'] });

        // Auth metadata is a fallback; a sync failure cannot undo a committed profile.
        const metadata = Object.fromEntries(Object.entries(updates).filter(([key]) => ['full_name', 'username', 'country', ...AVATAR_FIELDS].includes(key)));
        if (Object.keys(metadata).length) {
          try {
            const result = await supabase.auth.updateUser({ data: metadata });
            if (result.error) console.warn('Profile saved; metadata sync failed:', result.error.message);
          } catch (error) {
            console.warn('Profile saved; metadata sync failed:', error);
          }
        }
      } catch (error) {
        if (changesAvatar && sequence === avatarSequence.current && userRef.current?.id === owner.id) {
          pendingAvatar.current = null;
          profileRevision.current++;
          const restored = { ...userRef.current, ...confirmedAvatar.current };
          userRef.current = restored;
          setUser(restored);
          authStorage.saveUser(restored);
        }
        throw error;
      }
    };
    const request = profileQueue.current.then(write);
    profileQueue.current = request.catch(() => undefined);
    return request;
  }, []);

  const updatePassword = React.useCallback(async (newPassword: string) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return data;
  }, []);

  const value: AuthContextType = React.useMemo(() => ({
    user, session, isLoading, authReady,
    isAuthenticated: isOfflineMode ? !!user : (!!user && !!session),
    isOfflineMode,
    isLoggingIn, isSigningUp, isLoggingOut, isLoggingInWithGoogle, initStatus,
    loginError, signupError, logoutError, googleLoginError,
    needsAvatarMigration, dismissAvatarMigration,
    login, signup, logout, loginWithGoogle, deleteAccount,
    refreshSession, refreshProfile, refreshUser, resetPassword, updateProfile, updatePassword,
    clearErrors,
  }), [
    user, session, isLoading, authReady, isOfflineMode,
    isLoggingIn, isSigningUp, isLoggingOut, isLoggingInWithGoogle, initStatus,
    loginError, signupError, logoutError, googleLoginError,
    needsAvatarMigration, dismissAvatarMigration,
    login, signup, logout, loginWithGoogle, deleteAccount,
    refreshSession, refreshProfile, refreshUser, resetPassword, updateProfile, updatePassword,
    clearErrors
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

export const withAuth = <P extends object>(Component: React.ComponentType<P>) => {
  const AuthenticatedComponent = (props: P) => {
    const { isAuthenticated, isLoading, authReady } = useAuth();
    const [redirecting, setRedirecting] = useState(false);
    useEffect(() => {
      if (authReady && !isAuthenticated && !redirecting) {
        setRedirecting(true);
        window.location.replace("/login");
      }
    }, [authReady, isAuthenticated, redirecting]);
    if (isLoading || !authReady || redirecting) {
      const TierAwareLoadingScreen = React.lazy(() => import("@/components/TierAwareLoadingScreen"));
      return (
        <React.Suspense fallback={
          <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin mb-4"><div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full" /></div>
              <p className="text-muted-foreground">{isLoading ? "Authenticating..." : "Loading..."}</p>
            </div>
          </div>
        }>
          <TierAwareLoadingScreen message={isLoading ? "Authenticating..." : "Loading..."} />
        </React.Suspense>
      );
    }
    return <Component {...props} />;
  };
  AuthenticatedComponent.displayName = `withAuth(${Component.displayName || Component.name})`;
  return AuthenticatedComponent;
};

export const useAuthStatus = () => {
  const { isAuthenticated, isLoading, authReady, user, session } = useAuth();
  return { isAuthenticated, isLoading: isLoading || !authReady, isReady: authReady, user, session, hasUser: !!user, hasSession: !!session };
};

export default AuthContext;
